import { getAppNativeVars, getAppType } from '@/lib/offline/OfflineAppHelper';
import { useRegionStore } from '@/store/regionStore';
import { isDevelopment } from './appConstants';

// ============================================================================
// Region Types & Constants
// ============================================================================
export type Region = 'china' | 'global';

// Region to URL param mapping (china -> cn, global -> gl)
const REGION_URL_MAP: Record<Region, string> = { china: 'cn', global: 'gl' };
const getRegionString = (urlParam: string): Region | null =>
  (Object.entries(REGION_URL_MAP).find(([, v]) => v === urlParam)?.[0] as Region) ?? null;

const { VITE_WEB_URL, VITE_REGION, MODE } = import.meta.env;
const isDev = MODE === 'development';
const CHINA_TIMEZONE = 'Asia/Shanghai';

export const IS_CHINA_SITE = VITE_REGION === 'china';

// ============================================================================
// Region URL Helpers
// ============================================================================
export const getRegionUrl = (region: Region): string => {
  if (isDev) {
    const port = region === 'china' ? '5174' : '5173';
    return VITE_WEB_URL.replace(/:\d+/, `:${port}`) + window.location.pathname;
  }
  if (region === VITE_REGION) return VITE_WEB_URL;
  return region === 'china'
    ? VITE_WEB_URL.replace(/\.com$/, '.cn')
    : VITE_WEB_URL.replace(/\.cn$/, '.com');
};

export const redirectToRegion = (region: Region, rememberChoice = true): void => {
  // Use hash params to avoid Refine encoding the URL in 'to' param
  // Use short URL values (cn/gl) for cleaner URLs
  const baseUrl = getRegionUrl(region).split('?')[0];
  const hashParams = `ap_region=${REGION_URL_MAP[region]}${
    !rememberChoice ? '&remember=false' : ''
  }`;
  window.location.href = `${baseUrl}#${hashParams}`;
};

// ============================================================================
// Region Detection
// ============================================================================
export const isInChinaRegion = (): boolean => {
  const appType = getAppType();
  if (appType === 'mac' || appType === 'ios') {
    const country = getAppNativeVars(appType)?.country;
    if (country) return country.toLowerCase() === 'cn';
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone === CHINA_TIMEZONE;
};

// ============================================================================
// Region Redirect Logic
// ============================================================================
/**
 * Extract region params from URL hash (avoids Refine query param encoding)
 * URL uses short values (cn/gl), converts to internal Region type (china/global)
 */
const getRegionParamsFromUrl = (): { region: Region | null; rememberChoice: boolean } => {
  const hash = window.location.hash.slice(1); // Remove leading #
  if (hash) {
    const hashParams = new URLSearchParams(hash);
    const urlRegion = hashParams.get('ap_region');
    const remember = hashParams.get('remember');
    const region = urlRegion ? getRegionString(urlRegion) : null;
    if (region) {
      return { region, rememberChoice: remember !== 'false' };
    }
  }
  return { region: null, rememberChoice: true };
};

/**
 * Clean region params from URL hash
 */
export const cleanRegionParamsFromUrl = (): void => {
  if (window.location.hash) {
    const hashParams = new URLSearchParams(window.location.hash.slice(1));

    if (!hashParams.has('ap_region') && !hashParams.has('remember')) {
      return;
    }

    hashParams.delete('ap_region');
    hashParams.delete('remember');

    const remainingHash = hashParams.toString();
    const newUrl =
      window.location.pathname +
      window.location.search +
      (remainingHash ? `#${remainingHash}` : '');
    window.history.replaceState({}, '', newUrl);
  }
};

/**
 * Check and handle region redirect before auth check.
 * This runs early to prevent login page flash when redirect is needed.
 * Returns true if redirect is happening (caller should stop processing).
 */
export const checkAndHandleRedirectToRegion = (): boolean => {
  const { region: urlRegionParam, rememberChoice } = getRegionParamsFromUrl();

  // If URL has region param (in hash), process it and update store
  if (urlRegionParam) {
    // Only save preference if rememberChoice is true, otherwise leave store unchanged
    if (rememberChoice) {
      useRegionStore.getState().setRegionPreference(urlRegionParam);
    }
    // Don't redirect - user just arrived from a switch
    // Hash params are cleaned by SelectRegion component on mount
    return false;
  }

  if (isDevelopment) {
    return true;
  }
  // Check store for saved preference
  const storeRegion = useRegionStore.getState().regionPreference;

  if (storeRegion) {
    const isOnCorrectSite =
      (storeRegion === 'china' && IS_CHINA_SITE) || (storeRegion === 'global' && !IS_CHINA_SITE);

    if (!isOnCorrectSite) {
      redirectToRegion(storeRegion);
      return true; // Redirect happening
    }
  }

  return false; // No redirect needed
};

/**
 * Check if user should see region selection modal (China user on Global site).
 */
export const shouldShowRegionModal = (): boolean => {
  const hash = window.location.hash.slice(1);
  const hashParams = new URLSearchParams(hash);
  const hadUrlRegion = hashParams.has('ap_region');
  const storeRegion = useRegionStore.getState().regionPreference;

  return !IS_CHINA_SITE && isInChinaRegion() && !storeRegion && !hadUrlRegion;
};
